import { useMemo } from 'react';
import { Link, useLoaderData } from 'react-router';
import { asImageSrc } from '@prismicio/client';
import { motion, MotionConfig } from 'motion/react';

import type { Route } from './+types/topics';
import type { JourneyModule } from '@/lib/topic-state';
import { metaFor } from '@/cms/meta';
// The filtering helpers come from the client-safe module, because
// `clientLoader` runs in the browser bundle.
import { topicLanguage, topicModuleKey } from '@/cms/topics';
import { getTopics } from '@/cms/topics.server';
import { ActionBar } from '@/components/action-bar';
import { AppHeader, OverallProgress } from '@/components/header';
import { JourneyGround } from '@/components/journey/journey-ground';
import { JourneyComplete, JourneyIntro } from '@/components/journey/journey-intro';
import { ModuleCard } from '@/components/journey/module-card';
import { enterTransition } from '@/components/motion/tokens';
import { columnChild, columnVariants } from '@/components/motion/variants';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { useRootLoaderData } from '@/hooks/use-root-loader-data';
import { useStrings } from '@/i18n/use-strings';
import { deriveJourney, journeyRowsFor } from '@/lib/topic-state';
import { useChatProgressStore } from '@/store/chat-progress';
import { DEFAULT_BUDDY, themeForAudience, topicProfile, useUserStore } from '@/store/user';

export function meta({ matches }: Route.MetaArgs) {
  const root = matches[0]?.data;

  return root
    ? metaFor({ siteSettings: root.siteSettings, title: root.strings.journey.meta_journey })
    : [{ title: 'Beach Passport' }];
}

/**
 * The journey ships one flat row per topic — never the documents. A topic
 * carries every slice of a ten-minute module, and the trail needs nine fields.
 */
export async function loader() {
  const topics = await getTopics();

  const modules: Array<JourneyModule> = topics.map((topic) => ({
    uid: topic.uid,
    moduleKey: topicModuleKey(topic),
    theme: topic.data.theme ?? '',
    // Resolved here rather than shipped raw, so the client filter compares two
    // already-normalised codes.
    language: topicLanguage(topic) ?? '',
    order: topic.data.order ?? null,
    title: topic.data.title?.trim() || topic.uid,
    description: topic.data.description?.trim() || null,
    durationMinutes: topic.data.duration_minutes ?? null,
    iconUrl: asImageSrc(topic.data.icon),
    iconAlt: topic.data.icon?.alt ?? '',
  }));

  return { modules };
}

/**
 * The filter runs on the client because the profile lives on the device: the
 * server has no idea who this is.
 */
export async function clientLoader({ serverLoader }: Route.ClientLoaderArgs) {
  const { modules } = await serverLoader();
  const profile = topicProfile(useUserStore.getState());

  return { modules: profile ? journeyRowsFor(profile, modules) : [] };
}

clientLoader.hydrate = true as const;

/**
 * Invisible on purpose. The themed layout's own guard resolves before anything
 * paints, so its (null) fallback is what a visitor sees for that beat — a
 * skeleton here would never reach the screen. The trail's data is inlined in
 * the document by `clientLoader.hydrate`, so the first paint of this route is
 * already the real four cards, which is why there is no loading state to
 * reserve space for and no shift when the data "arrives".
 */
export function HydrateFallback() {
  return null;
}

export default function Journey() {
  // `clientLoader` always runs (`hydrate`), so its shape is the one that lands.
  const { modules } = useLoaderData<typeof clientLoader>();
  const { journey } = useStrings();
  const { siteSettings } = useRootLoaderData();

  const profile = useUserStore((state) => state.profile);
  const progress = useUserStore((state) => state.modules);
  const runs = useChatProgressStore((state) => state.runs);

  // Derived, not stored: finishing a module updates the store, and the trail
  // restates itself without a revalidation. The same goes for a run in
  // progress — leaving a module half-done re-draws the card on the way back.
  const trail = useMemo(() => {
    const done = new Set(
      Object.entries(progress).flatMap(([key, value]) => (value.bestScore === undefined ? [] : [key])),
    );

    const started = new Map(
      Object.entries(runs).flatMap(([key, run]) => (run.progress === undefined ? [] : [[key, run.progress] as const])),
    );

    return deriveJourney(modules, done, started);
  }, [modules, progress, runs]);

  const theme = themeForAudience(profile?.audience ?? 'adult');

  return (
    // `reducedMotion="user"` collapses every animation below to opacity and
    // colour without changing a rendered attribute — the same configuration
    // onboarding runs under, so the two halves of the app honour the
    // preference identically.
    <MotionConfig reducedMotion="user" transition={enterTransition}>
      <AppHeader
        variant="full"
        wide
        progress={
          // Nothing to show until the client filter has run: an empty bar is
          // quieter than four grey segments that turn out to be three.
          trail.total === 0 ? null : (
            <OverallProgress
              done={trail.done}
              total={trail.total}
              labels={trail.steps.map((step) => step.title)}
              // Each segment reports its own module, so the bar and the cards
              // below it cannot disagree.
              segments={trail.steps.map((step) => ({
                done: step.state === 'done',
                fraction: step.progress?.fraction ?? 0,
              }))}
            />
          )
        }
      />

      <JourneyGround src={siteSettings.secondaryBackgroundUrl} />

      {/*
        The composition. On a phone it is one column with the primary action
        pinned to the bottom edge, in the thumb zone. From `lg` the page becomes
        two columns — the greeting panel and the action on the left, the trail on
        the right — because a 42 rem column of cards centred on a 1440 px field
        was the thing that read as unfinished.

        The desktop grid is also what keeps the page at zero layout shift: the
        action sits in the *left* column's second row, so a three-module trail
        and a four-module trail put it in exactly the same place.

        The measure is `AppHeader`'s, to the pixel: the same `max-w-2xl`,
        the same `lg:max-w-5xl` under `wide`, and the same `px-4 sm:px-6`
        gutters. It used to run `px-5 sm:px-8` and then centre a
        shrink-to-fit `18rem + 29rem` grid inside that, which left the greeting
        panel starting 116 px inside the header's logo and the trail ending
        92 px inside the progress rule — the gap the product owner photographed
        (ticket 23 §1). The trail column now takes the rest of the measure
        instead of being capped, so both columns end on the header's edges.
      */}
      <motion.main
        id="main-content"
        variants={columnVariants}
        initial="hidden"
        animate="shown"
        className="relative z-10 mx-auto flex w-full max-w-2xl flex-1 flex-col px-4 pt-5 pb-14 sm:px-6 lg:grid lg:max-w-5xl lg:grid-cols-[18rem_minmax(0,1fr)] lg:grid-rows-[auto_auto] lg:content-center lg:items-start lg:gap-x-10 lg:py-10"
      >
        {trail.allDone ? <JourneyComplete theme={theme} buddy={profile?.buddy ?? DEFAULT_BUDDY} /> : null}

        {trail.allDone ? null : (
          <JourneyIntro
            buddy={profile?.buddy ?? DEFAULT_BUDDY}
            name={profile?.name}
            total={trail.total}
            showReward={trail.total > 0}
          />
        )}

        {trail.total === 0 ? (
          <motion.p
            {...columnChild}
            dir="auto"
            className="journey-panel mt-6 rounded-3xl p-5 text-sm text-pretty text-white/85 lg:col-start-2 lg:row-start-1 lg:mt-0"
          >
            {journey.journey_empty}
          </motion.p>
        ) : (
          <ol className="mt-6 space-y-4 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:mt-0">
            {trail.steps.map((step, index) => (
              <ModuleCard
                key={step.uid}
                step={step}
                href={`/h/${theme}/learn/topics/${step.uid}`}
                score={progress[step.moduleKey]?.bestScore}
                isLast={index === trail.steps.length - 1}
              />
            ))}
          </ol>
        )}

        {/*
          The screen's one action, and the only yellow on the page apart from
          the trail behind the learner. Rendered last in the DOM so nothing sits
          under the bar on a phone; placed into the left column from `lg`, where
          there is no thumb to reach with.

          When the course is unfinished it starts the recommended module and
          names it, so the bar answers "what am I about to do" rather than just
          "go". When it is finished it claims the passport.
        */}
        {trail.allDone ? (
          <ActionBar className="lg:col-start-1 lg:row-start-2">
            <Button asChild variant="brand-yellow" size="xl">
              <Link to={`/h/${theme}/passport`} viewTransition>
                {journey.journey_get_passport}
                <Icon name="passport/arrow-right" aria-hidden className="size-5 rtl:rotate-180" />
              </Link>
            </Button>
          </ActionBar>
        ) : trail.next ? (
          <ActionBar className="lg:col-start-1 lg:row-start-2">
            <p dir="auto" className="mb-2 flex items-baseline gap-2 text-xs font-bold tracking-[0.08em] uppercase">
              <span className="text-surf-yellow">{journey.journey_module_next}</span>
              <span className="truncate text-white/75">{trail.next.title}</span>
            </p>

            <Button asChild variant="brand-yellow" size="xl">
              <Link to={`/h/${theme}/learn/topics/${trail.next.uid}`} viewTransition>
                {/* A part-played module resumes where it was left, so the bar says so. */}
                {trail.next.progress ? journey.journey_continue_module : journey.journey_start_module}
                <Icon name="passport/arrow-right" aria-hidden className="size-5 rtl:rotate-180" />
              </Link>
            </Button>
          </ActionBar>
        ) : null}
      </motion.main>
    </MotionConfig>
  );
}
